<template>
  <div>
    <v-form v-model="valid" ref="form" lazy-validation @submit="authenticate">
      <v-container class="pt-0">
        <span class="headline">{{$t('common.login')}}</span>
        <v-card>
          <v-card-text class="success" v-if="successMessage">
            <span>
              <v-icon>mdi-checkbox-marked-circle</v-icon>
              &nbsp;{{successMessage}}
            </span>
          </v-card-text>
        </v-card>
        <v-row>
          <v-col>
            <v-text-field
              data-test-id="email"
              ref="email"
              v-model="email"
              type="email"
              name="email"
              :disabled="loading"
              :rules="emailRules"
              autocomplete="username"
              prepend-icon="mdi-account"
              :label="$t('common.email-address')"
              required
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              data-test-id="password"
              v-model="password"
              :disabled="loading"
              id="password"
              :label="$t('common.password')"
              autocomplete="current-password"
              :rules="passwordRules"
              name="password"
              prepend-icon="mdi-lock"
              type="password"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              data-test-id="submitButton"
              type="submit"
              color="primary"
              :disabled="loading"
              :loading="loading"
            >
              <v-icon left>mdi-login</v-icon>{{$t('common.login')}}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              class="mr-4"
              :disabled="loading"
              link
              :to="'/forgotPassword' + ($route.query.redirect ? '?redirect=' + encodeURIComponent($route.query.redirect) : '')"
              data-test-id="forgotPasswordButton"
            >
              <v-icon left>mdi-lock-question</v-icon>{{$t('common.forgot-password')}}
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="mt-5">
          <v-col cols="12">
            <span class="overline">{{$t('registration.register-now')}}</span>
            <br />
            <v-btn
              class="secondary"
              :disabled="loading"
              link
              :to="'/register' + ($route.query.redirect ? '?redirect=' + encodeURIComponent($route.query.redirect) : '')"
              data-test-id="registerButton"
            >
              <v-icon left>mdi-account-plus</v-icon>{{$t('registration.sign-up')}}
            </v-btn>
          </v-col>
          <v-col cols="12">
            <span class="overline">{{$t('verification.link-expired-or-not-received')}}</span>
            <br />
            <v-btn
              class=""
              :disabled="loading"
              link
              :to="'/resendVerification' + ($route.query.redirect ? '?redirect=' + encodeURIComponent($route.query.redirect) : '')"
              data-test-id="resendVerificationButton"
            >
              <v-icon left>mdi-account-check</v-icon>{{$t('verification.re-verification')}}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </div>
</template>

<script>
import { MUTATION_LOGIN, ACTION_INIT, MUTATION_RAISE_NOTIFICATION, MUTATION_RAISE_NEWSLETTER_MODAL } from "@/plugins/store.js";
import { EVENT_LOGOUT_AFTER } from "@/plugins/eventbus.js";

export default {
  name: "Login",
  data: () => ({
    valid: false,
    loading: false,
    email: "",
    emailRules: [
      v => !!v || "E-mail is required",
      v => /.+@.+\..+/.test(v) || "E-mail must be valid"
    ],
    password: "",
    passwordRules: [v => !!v || "Password is required"],
    successMessage: undefined,
    error: undefined
  }),

  mounted() {
    this.email = this.$store.state.auth.emailAddress;
    // console.log(this.$route.query);
    if (this.$route.query.resetPasswordSuccess == "true") {
      this.successMessage =
        "Dein Passwort wurde erfolgreich gesetzt. Du kannst dich nun anmelden.";
    }
    if (this.$route.query.emailVerified == "true") {
      this.successMessage =
        "Deine E-Mail-Adresse wurde erfolgreich bestätigt. Du kannst dich nun anmelden.";
    }
    window.setTimeout(
      function() {
        this.$refs["email"].focus();
      }.bind(this),
      0
    );
    this.$eventBus.$on(EVENT_LOGOUT_AFTER, this.onLogoutAfter);
  },

  beforeDestroy() {
    // console.log("Login::beforeDestroy");
    this.$eventBus.$off(EVENT_LOGOUT_AFTER, this.onLogoutAfter);
  },

  computed: {
    filled() {
      return (
        this.email &&
        this.email.length > 0 &&
        this.password &&
        this.password.length > 0
      );
    }
  },

  methods: {
    validate() {
      this.$refs.form.validate();
    },
    onLogoutAfter() {
      // console.log("Login::onLogoutAfter");
      this.reset();
      this.email = this.$store.state.auth.emailAddress;
    },
    authenticate(e) {
      e.preventDefault();
      var formData = new FormData(e.currentTarget);
      var email = formData.get('email')
      var password = formData.get('password')
      var validationErrorMsg = null;
      if (email == null || email == '') {
        validationErrorMsg = 'Bitte gib Deine E-Mail-Adresse ein';
      } else if (password == null || password == '') {
        validationErrorMsg = 'Bitte gib Dein Passwort ein';
      }
      if (validationErrorMsg) {
        this.$store.commit(MUTATION_RAISE_NOTIFICATION, {
          type: "info",
          message: validationErrorMsg
        });
        return;
      }
      // console.log("Authenticating...");
      this.loading = true;
      this.$http
        .post("/api/user/auth", {
          email: email,
          password: password
        })
        .then(response => {
          this.loading = false;
          // console.log(response);
          var data = response.data;
          this.$store.commit(MUTATION_LOGIN, {
            email: this.email,
            accessToken: data.accessToken,
            user: data.user
          });
          this.$store.dispatch(ACTION_INIT);
          if(!data.user.isSubscriber && !data.user.cancelledNewsletterModalAt){
            this.$store.commit(MUTATION_RAISE_NEWSLETTER_MODAL)
          }
          this.password = null;
          if (this.$route.query.redirect) {
            this.$router.appSecurePush(this.$route.query.redirect);
          } else {
            this.$router.push("/dashboard");
          }
        })
        .catch(error => {
          // also catches the store authorized error
          this.loading = false;
          this.$common.onError(error)
        });
      return false;
    },
    reset() {
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    }
  }
};
</script>
